import { useEffect, useState } from "react";
import { en, type Messages } from "./locales/en";
import { zhHans } from "./locales/zh-Hans";
import { zhHant } from "./locales/zh-Hant";

export type { Messages } from "./locales/en";

export const LOCALES = ["en", "zh-Hant", "zh-Hans"] as const;

export type LocaleCode = (typeof LOCALES)[number];

export const LOCALE_LABELS: Record<LocaleCode, string> = {
  en: "English",
  "zh-Hant": "繁體中文",
  "zh-Hans": "简体中文",
};

const STORAGE_KEY = "calendar-app.locale";

const catalogs: Record<LocaleCode, Messages> = {
  en,
  "zh-Hant": zhHant,
  "zh-Hans": zhHans,
};

type Paths<T> = {
  [K in keyof T & string]: T[K] extends string
    ? K
    : T[K] extends Record<string, unknown>
      ? `${K}.${Paths<T[K]>}`
      : never;
}[keyof T & string];

export type MessagePath = Paths<Messages>;

const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g;

function isLocaleCode(value: string | null): value is LocaleCode {
  return value === "en" || value === "zh-Hant" || value === "zh-Hans";
}

function readStoredLocale(): LocaleCode | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isLocaleCode(value) ? value : null;
  } catch {
    return null;
  }
}

function detectLocale(): LocaleCode {
  const stored = readStoredLocale();
  if (stored) {
    return stored;
  }
  if (typeof navigator === "undefined") {
    return "en";
  }
  const lang = (navigator.language || "en").toLowerCase();
  if (!lang.startsWith("zh")) {
    return "en";
  }
  if (
    lang.includes("tw") ||
    lang.includes("hk") ||
    lang.includes("mo") ||
    lang.includes("hant")
  ) {
    return "zh-Hant";
  }
  return "zh-Hans";
}

let currentLocale: LocaleCode = detectLocale();
const listeners = new Set<() => void>();

function applyDocumentLang(locale: LocaleCode) {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.lang = locale;
}

applyDocumentLang(currentLocale);

export function getLocale(): LocaleCode {
  return currentLocale;
}

export function setLocale(next: LocaleCode) {
  if (!isLocaleCode(next) || next === currentLocale) {
    return;
  }
  currentLocale = next;
  applyDocumentLang(next);
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Ignore storage failures; the in-memory locale still applies.
  }
  listeners.forEach((listener) => listener());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function lookup(messages: Messages, path: string): string {
  const parts = path.split(".");
  let node: unknown = messages;
  for (const part of parts) {
    if (!node || typeof node !== "object" || !(part in node)) {
      return path;
    }
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === "string" ? node : path;
}

export function t(
  key: MessagePath,
  params?: Record<string, string | number>,
): string {
  const template = lookup(catalogs[currentLocale], key);
  if (!params) {
    return template;
  }
  return template.replace(PLACEHOLDER, (_, name: string) => {
    const value = params[name];
    return value === undefined || value === null ? "" : String(value);
  });
}

export function useI18n() {
  const [locale, setLocaleState] = useState(getLocale);
  useEffect(() => subscribe(() => setLocaleState(getLocale())), []);
  return { locale, setLocale, t };
}
